
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  Check,
  Flag,
  Heart,
  LogOut,
  Mail,
  RefreshCw,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";

import { supabase } from "./lib/supabase";
import "./Admin.css";

function Admin() {
  const [session, setSession] = useState(null);

  const [email, setEmail] = useState("");
  const [signInMessage, setSignInMessage] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [pendingNotes, setPendingNotes] = useState([]);
  const [pendingReplies, setPendingReplies] = useState([]);
  const [reports, setReports] = useState([]);

  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);

  const [pageMessage, setPageMessage] = useState("");
  const [activeAction, setActiveAction] = useState("");

  const [refreshing, setRefreshing] = useState(false);

  // --------------------------------------------------
  // SESSION
  // --------------------------------------------------

  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      const {
        data: { session: currentSession },
        error,
      } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error) {
        console.error("Unable to load session:", error);
      }

      setSession(currentSession);
    };

    loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        if (mounted) {
          setSession(nextSession);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // --------------------------------------------------
  // ADMIN ACCESS
  // --------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    if (!session) {
      setCheckingAdmin(false);
      setIsAdmin(false);
      return;
    }

    const checkAdminStatus = async () => {
      setCheckingAdmin(true);
      setPageMessage("");

      const { data, error } = await supabase.rpc(
        "is_admin"
      );

      if (cancelled) return;

      if (error) {
        console.error(
          "Unable to verify administrator:",
          error
        );

        setIsAdmin(false);
        setPageMessage(
          "We couldn't verify administrator access."
        );
      } else {
        setIsAdmin(Boolean(data));
      }

      setCheckingAdmin(false);
    };

    checkAdminStatus();

    return () => {
      cancelled = true;
    };
  }, [session]);

  // --------------------------------------------------
  // AUTHENTICATION ACTIONS
  // --------------------------------------------------

  const handleSignIn = async (event) => {
    event.preventDefault();

    const cleanEmail = email.trim();

    if (!cleanEmail || signingIn) return;

    setSigningIn(true);
    setSignInMessage("");

    try {
      const { error } =
        await supabase.auth.signInWithOtp({
          email: cleanEmail,
          options: {
            emailRedirectTo:
              `${window.location.origin}/admin`,
          },
        });

      if (error) {
        console.error(
          "Unable to send admin login:",
          error
        );

        if (
          error.message
            ?.toLowerCase()
            .includes("rate")
        ) {
          setSignInMessage(
            "Too many sign-in links were requested. Please wait a little while and try again."
          );
        } else {
          setSignInMessage(
            "The sign-in link couldn't be sent. Please try again."
          );
        }

        return;
      }

      setSignInMessage(
        "Check your email. Your private sign-in link is on its way."
      );

      setEmail("");
    } catch (error) {
      console.error("Sign-in error:", error);

      setSignInMessage(
        "Something went wrong. Please try again."
      );
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    if (activeAction) return;

    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error("Unable to sign out:", error);

      setPageMessage(
        "Sign-out failed. Please try again."
      );

      return;
    }

    setIsAdmin(false);
    setPendingNotes([]);
    setPendingReplies([]);
    setReports([]);
    setPageMessage("");
    setActiveAction("");
  };

  // --------------------------------------------------
  // LOAD PENDING LOVE NOTES
  // --------------------------------------------------

  const loadPendingNotes = useCallback(async () => {
    setLoadingNotes(true);

    try {
      const { data, error } = await supabase
        .from("love_messages")
        .select(`
          id,
          recipient,
          message,
          author_name,
          category,
          visibility,
          moderation_status,
          created_at
        `)
        .eq("moderation_status", "pending")
        .order("created_at", {
          ascending: true,
        });

      if (error) throw error;

      setPendingNotes(data || []);
    } catch (error) {
      console.error(
        "Unable to load pending notes:",
        error
      );

      setPageMessage(
        "The pending love notes couldn't be loaded."
      );
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  // --------------------------------------------------
  // LOAD PENDING LOVE REPLIES
  // --------------------------------------------------

  const loadPendingReplies = useCallback(async () => {
    setLoadingReplies(true);

    try {
      const { data, error } = await supabase
        .from("love_message_replies")
        .select(`
          id,
          message,
          author_name,
          moderation_status,
          created_at,
          love_messages (
            id,
            message,
            recipient
          )
        `)
        .eq("moderation_status", "pending")
        .order("created_at", {
          ascending: true,
        });

      if (error) throw error;

      setPendingReplies(data || []);
    } catch (error) {
      console.error(
        "Unable to load pending replies:",
        error
      );

      setPageMessage(
        "Pending love replies couldn't be loaded."
      );
    } finally {
      setLoadingReplies(false);
    }
  }, []);

  // --------------------------------------------------
  // LOAD REPORTED LOVE NOTES
  // --------------------------------------------------

  const loadReportedNotes = useCallback(async () => {
    setLoadingReports(true);

    try {
      const { data, error } = await supabase
        .from("love_message_reports")
        .select(`
          id,
          message_id,
          reason,
          details,
          created_at,
          love_messages (
            id,
            recipient,
            message,
            author_name,
            category,
            visibility,
            moderation_status,
            created_at
          )
        `)
        .order("created_at", {
          ascending: true,
        });

      if (error) throw error;

      setReports(data || []);
    } catch (error) {
      console.error(
        "Unable to load reported notes:",
        error
      );

      setPageMessage(
        "Reported love notes couldn't be loaded."
      );
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // --------------------------------------------------
  // INITIAL DASHBOARD LOAD
  // --------------------------------------------------

  useEffect(() => {
    if (!isAdmin) return;

    Promise.all([
      loadPendingNotes(),
      loadPendingReplies(),
      loadReportedNotes(),
    ]);
  }, [
    isAdmin,
    loadPendingNotes,
    loadPendingReplies,
    loadReportedNotes,
  ]);

  // --------------------------------------------------
  // REFRESH ALL MODERATION QUEUES
  // --------------------------------------------------

  const refreshEverything = async () => {
    if (refreshing || activeAction) return;

    setRefreshing(true);
    setPageMessage("");

    try {
      await Promise.all([
        loadPendingNotes(),
        loadPendingReplies(),
        loadReportedNotes(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // --------------------------------------------------
  // SHARED MODERATION HELPER
  // --------------------------------------------------

  const updateModerationStatus = async ({
    table,
    id,
    status,
    onSuccess,
    successMessage,
    errorMessage,
    actionKey,
  }) => {
    if (activeAction) return;

    setActiveAction(actionKey);
    setPageMessage("");

    try {
      const { data, error } = await supabase
        .from(table)
        .update({
          moderation_status: status,
          approved_at:
            status === "approved"
              ? new Date().toISOString()
              : null,
        })
        .eq("id", id)
        .eq("moderation_status", "pending")
        .select("id");

      if (error) throw error;

      if (!data?.length) {
        throw new Error(
          "No pending record was updated."
        );
      }

      onSuccess();

      setPageMessage(successMessage);
    } catch (error) {
      console.error(
        "Unable to update moderation status:",
        error
      );

      setPageMessage(errorMessage);
    } finally {
      setActiveAction("");
    }
  };

  // --------------------------------------------------
  // APPROVE / REJECT LOVE NOTES
  // --------------------------------------------------

  const approveNote = (noteId) =>
    updateModerationStatus({
      table: "love_messages",
      id: noteId,
      status: "approved",
      actionKey: `approve-note-${noteId}`,
      onSuccess: () => {
        setPendingNotes((current) =>
          current.filter(
            (note) => note.id !== noteId
          )
        );
      },
      successMessage:
        "Love note approved. It can now appear on the Community Wall.",
      errorMessage:
        "That love note couldn't be approved.",
    });

  const rejectNote = (noteId) =>
    updateModerationStatus({
      table: "love_messages",
      id: noteId,
      status: "rejected",
      actionKey: `reject-note-${noteId}`,
      onSuccess: () => {
        setPendingNotes((current) =>
          current.filter(
            (note) => note.id !== noteId
          )
        );
      },
      successMessage: "Love note rejected.",
      errorMessage:
        "That love note couldn't be rejected.",
    });

  // --------------------------------------------------
  // APPROVE / REJECT LOVE REPLIES
  // --------------------------------------------------

  const approveReply = (replyId) =>
    updateModerationStatus({
      table: "love_message_replies",
      id: replyId,
      status: "approved",
      actionKey: `approve-reply-${replyId}`,
      onSuccess: () => {
        setPendingReplies((current) =>
          current.filter(
            (reply) => reply.id !== replyId
          )
        );
      },
      successMessage:
        "Love reply approved! It can now appear beneath its original love note.",
      errorMessage:
        "That love reply couldn't be approved.",
    });

  const rejectReply = (replyId) =>
    updateModerationStatus({
      table: "love_message_replies",
      id: replyId,
      status: "rejected",
      actionKey: `reject-reply-${replyId}`,
      onSuccess: () => {
        setPendingReplies((current) =>
          current.filter(
            (reply) => reply.id !== replyId
          )
        );
      },
      successMessage: "Love reply rejected.",
      errorMessage:
        "That love reply couldn't be rejected.",
    });

  // --------------------------------------------------
  // DISMISS REPORTS
  // --------------------------------------------------

  const dismissReports = async (messageId) => {
    if (activeAction) return;

    setActiveAction(`dismiss-${messageId}`);
    setPageMessage("");

    try {
      const { error } = await supabase
        .from("love_message_reports")
        .delete()
        .eq("message_id", messageId);

      if (error) throw error;

      setReports((current) =>
        current.filter(
          (report) =>
            report.message_id !== messageId
        )
      );

      setPageMessage(
        "Reports dismissed. The love note remains on the Community Wall."
      );
    } catch (error) {
      console.error(
        "Unable to dismiss reports:",
        error
      );

      setPageMessage(
        "Those reports couldn't be dismissed."
      );
    } finally {
      setActiveAction("");
    }
  };

  // --------------------------------------------------
  // REMOVE REPORTED LOVE NOTE
  // --------------------------------------------------

  const removeReportedNote = async (messageId) => {
    if (activeAction) return;

    setActiveAction(`remove-${messageId}`);
    setPageMessage("");

    try {
      const { error: noteError } = await supabase
        .from("love_messages")
        .update({
          moderation_status: "rejected",
          approved_at: null,
        })
        .eq("id", messageId);

      if (noteError) throw noteError;

      const { error: reportError } = await supabase
        .from("love_message_reports")
        .delete()
        .eq("message_id", messageId);

      if (reportError) {
        console.error(
          "Note removed, but reports could not be cleared:",
          reportError
        );

        setPageMessage(
          "The love note was removed, but its report records still need review."
        );

        await loadReportedNotes();
        return;
      }

      setReports((current) =>
        current.filter(
          (report) =>
            report.message_id !== messageId
        )
      );

      setPageMessage(
        "The reported note was removed from the Community Wall."
      );
    } catch (error) {
      console.error(
        "Unable to remove reported note:",
        error
      );

      setPageMessage(
        "That note couldn't be removed from the wall."
      );
    } finally {
      setActiveAction("");
    }
  };

  // --------------------------------------------------
  // GROUP REPORTS BY LOVE NOTE
  // --------------------------------------------------

  const reportedNoteGroups = useMemo(() => {
    const grouped = new Map();

    reports.forEach((report) => {
      const message = report.love_messages;

      if (!message) return;

      if (!grouped.has(report.message_id)) {
        grouped.set(report.message_id, {
          message,
          reports: [],
        });
      }

      grouped
        .get(report.message_id)
        .reports.push(report);
    });

    return Array.from(grouped.values());
  }, [reports]);

  const isBusy = Boolean(activeAction);

  // --------------------------------------------------
  // LOGIN SCREEN
  // --------------------------------------------------

  if (!session) {
    return (
      <main className="adminPage">
        <section className="adminLoginCard">
          <div className="adminHeart">
            <Heart
              size={25}
              fill="currentColor"
            />
          </div>

          <p className="adminEyebrow">
            I Love You So Much
          </p>

          <h1>Love Wall Admin</h1>

          <p className="adminIntroduction">
            A private little place for keeping
            the Community Wall loving, gentle,
            and kind.
          </p>

          <form
            className="adminLoginForm"
            onSubmit={handleSignIn}
          >
            <label htmlFor="admin-email">
              Administrator email
            </label>

            <div className="adminEmailField">
              <Mail size={18} />

              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(event) =>
                  setEmail(event.target.value)
                }
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <button
              type="submit"
              className="adminPrimaryButton"
              disabled={
                signingIn || !email.trim()
              }
            >
              <Heart
                size={16}
                fill="currentColor"
              />

              {signingIn
                ? "Sending..."
                : "Send my sign-in link"}
            </button>
          </form>

          {signInMessage && (
            <p
              className="adminStatus"
              role="status"
            >
              {signInMessage}
            </p>
          )}

          <a
            className="adminBackLink"
            href="/"
          >
            ← Back to the Community Wall
          </a>
        </section>
      </main>
    );
  }

  // --------------------------------------------------
  // CHECKING ACCESS
  // --------------------------------------------------

  if (checkingAdmin) {
    return (
      <main className="adminPage">
        <div className="adminLoading">
          <Heart
            size={22}
            fill="currentColor"
          />
          Checking access...
        </div>
      </main>
    );
  }

  // --------------------------------------------------
  // ACCESS DENIED
  // --------------------------------------------------

  if (!isAdmin) {
    return (
      <main className="adminPage">
        <section className="adminLoginCard">
          <ShieldCheck size={34} />

          <h1>
            Administrator access required
          </h1>

          <p>
            You are signed in, but this account
            has not been given permission to
            moderate the Community Wall.
          </p>

          <button
            type="button"
            className="adminSecondaryButton"
            onClick={handleSignOut}
          >
            Sign out
          </button>
        </section>
      </main>
    );
  }

  // --------------------------------------------------
  // ADMIN DASHBOARD
  // --------------------------------------------------

  return (
    <div className="adminPage">
      <header className="adminHeader">
        <div>
          <p className="adminEyebrow">
            I Love You So Much
          </p>

          <h1>Community Moderation</h1>

          <p>
            Keep this little corner of the
            internet loving, gentle, and kind.
          </p>
        </div>

        <div className="adminHeaderActions">
          <button
            type="button"
            className="adminIconButton"
            onClick={refreshEverything}
            disabled={refreshing || isBusy}
            aria-label="Refresh moderation queues"
            title="Refresh moderation queues"
          >
            <RefreshCw size={18} />
          </button>

          <button
            type="button"
            className="adminSecondaryButton"
            onClick={handleSignOut}
            disabled={isBusy}
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </header>

      <main className="adminContent">

        {/* ----------------------------------------
            DASHBOARD OVERVIEW
        ---------------------------------------- */}

        <section className="adminOverview">
          <div className="adminOverviewCard">
            <Heart
              size={20}
              fill="currentColor"
            />

            <div>
              <strong>
                {pendingNotes.length}
              </strong>
              <span>pending notes</span>
            </div>
          </div>

          <div className="adminOverviewCard">
            <Mail size={20} />

            <div>
              <strong>
                {pendingReplies.length}
              </strong>
              <span>pending replies</span>
            </div>
          </div>

          <div className="adminOverviewCard adminOverviewAlert">
            <Flag size={20} />

            <div>
              <strong>
                {reportedNoteGroups.length}
              </strong>
              <span>reported notes</span>
            </div>
          </div>
        </section>

        {/* STATUS MESSAGES */}

        {pageMessage && (
          <div
            className="adminStatus adminPageStatus"
            role="status"
            aria-live="polite"
          >
            {pageMessage}
          </div>
        )}

        {/* ----------------------------------------
            PENDING LOVE NOTES
        ---------------------------------------- */}

        <section className="adminModerationSection">
          <div className="adminQueueHeading">
            <div>
              <p className="adminEyebrow">
                Waiting for a little review
              </p>

              <h2>Pending Love Notes</h2>
            </div>

            <div className="pendingCount">
              {pendingNotes.length}
            </div>
          </div>

          {loadingNotes ? (
            <div className="adminEmptyState">
              <Heart
                size={22}
                fill="currentColor"
              />
              Gathering the pending love...
            </div>
          ) : pendingNotes.length === 0 ? (
            <div className="adminEmptyState">
              <Heart
                size={30}
                fill="currentColor"
              />

              <h3>All caught up.</h3>

              <p>
                There are no new love notes
                waiting for review right now.
              </p>
            </div>
          ) : (
            <div className="moderationGrid">
              {pendingNotes.map((note) => {
                const approving =
                  activeAction ===
                  `approve-note-${note.id}`;

                const rejecting =
                  activeAction ===
                  `reject-note-${note.id}`;

                return (
                  <article
                    className="moderationCard"
                    key={note.id}
                  >
                    <div className="moderationCardTop">
                      <span className="moderationCategory">
                        {note.category}
                      </span>

                      <span className="moderationDate">
                        {new Date(
                          note.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="moderationRecipient">
                      For {note.recipient}
                    </p>

                    <blockquote>
                      “{note.message}”
                    </blockquote>

                    <p className="moderationAuthor">
                      — {note.author_name || "Anonymous"}
                    </p>

                    <div className="moderationActions">
                      <button
                        type="button"
                        className="rejectButton"
                        disabled={isBusy}
                        onClick={() =>
                          rejectNote(note.id)
                        }
                      >
                        <X size={17} />

                        {rejecting
                          ? "Rejecting..."
                          : "Reject"}
                      </button>

                      <button
                        type="button"
                        className="approveButton"
                        disabled={isBusy}
                        onClick={() =>
                          approveNote(note.id)
                        }
                      >
                        <Check size={17} />

                        {approving
                          ? "Approving..."
                          : "Approve"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* ----------------------------------------
            PENDING LOVE REPLIES
        ---------------------------------------- */}

        <section className="adminModerationSection">
          <div className="adminQueueHeading">
            <div>
              <p className="adminEyebrow">
                A little love coming back
              </p>

              <h2>Pending Love Replies</h2>

              <p className="queueDescription">
                Review the kind messages visitors
                have sent in response to Community
                Love Notes.
              </p>
            </div>

            <div className="pendingCount">
              {pendingReplies.length}
            </div>
          </div>

          {loadingReplies ? (
            <div className="adminEmptyState">
              <Heart
                size={22}
                fill="currentColor"
              />

              Gathering the love replies...
            </div>
          ) : pendingReplies.length === 0 ? (
            <div className="adminEmptyState">
              <Heart
                size={30}
                fill="currentColor"
              />

              <h3>All caught up.</h3>

              <p>
                There are no love replies waiting
                for review right now.
              </p>
            </div>
          ) : (
            <div className="moderationGrid">
              {pendingReplies.map((reply) => {
                const approving =
                  activeAction ===
                  `approve-reply-${reply.id}`;

                const rejecting =
                  activeAction ===
                  `reject-reply-${reply.id}`;

                const originalNote =
                  reply.love_messages;

                return (
                  <article
                    className="moderationCard"
                    key={reply.id}
                  >
                    <div className="moderationCardTop">
                      <span className="moderationCategory">
                        Love Reply
                      </span>

                      <span className="moderationDate">
                        {new Date(
                          reply.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="moderationRecipient">
                      Replying to the note for{" "}
                      {originalNote?.recipient ||
                        "someone special"}
                    </p>

                    <blockquote>
                      “{originalNote?.message ||
                        "Original note unavailable"}”
                    </blockquote>

                    <p className="moderationRecipient">
                      Visitor's reply
                    </p>

                    <blockquote>
                      “{reply.message}”
                    </blockquote>

                    <p className="moderationAuthor">
                      — {reply.author_name || "Anonymous"}
                    </p>

                    <div className="moderationActions">
                      <button
                        type="button"
                        className="rejectButton"
                        disabled={isBusy}
                        onClick={() =>
                          rejectReply(reply.id)
                        }
                      >
                        <X size={17} />

                        {rejecting
                          ? "Rejecting..."
                          : "Reject"}
                      </button>

                      <button
                        type="button"
                        className="approveButton"
                        disabled={isBusy}
                        onClick={() =>
                          approveReply(reply.id)
                        }
                      >
                        <Check size={17} />

                        {approving
                          ? "Approving..."
                          : "Approve"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* ----------------------------------------
            REPORTED LOVE NOTES
        ---------------------------------------- */}

        <section className="adminModerationSection reportedQueueSection">
          <div className="adminQueueHeading">
            <div>
              <p className="adminEyebrow">
                Community reports
              </p>

              <h2>Reported Love Notes</h2>

              <p className="queueDescription">
                Review notes that visitors have
                flagged after they appeared on
                the Community Wall.
              </p>
            </div>

            <div className="reportedCount">
              {reportedNoteGroups.length}
            </div>
          </div>

          {loadingReports ? (
            <div className="adminEmptyState">
              <Flag size={22} />
              Gathering community reports...
            </div>
          ) : reportedNoteGroups.length === 0 ? (
            <div className="adminEmptyState">
              <Heart
                size={30}
                fill="currentColor"
              />

              <h3>Nothing troubling here.</h3>

              <p>
                No approved love notes have
                active community reports.
              </p>
            </div>
          ) : (
            <div className="reportedNotesList">
              {reportedNoteGroups.map(
                ({
                  message,
                  reports: noteReports,
                }) => {
                  const dismissing =
                    activeAction ===
                    `dismiss-${message.id}`;

                  const removing =
                    activeAction ===
                    `remove-${message.id}`;

                  return (
                    <article
                      className="reportedModerationCard"
                      key={message.id}
                    >
                      <div className="reportWarningHeader">
                        <div>
                          <div className="reportBadge">
                            <AlertTriangle
                              size={15}
                            />

                            {noteReports.length}{" "}
                            {noteReports.length === 1
                              ? "report"
                              : "reports"}
                          </div>

                          <p className="moderationRecipient">
                            For {message.recipient}
                          </p>
                        </div>

                        <span className="moderationCategory">
                          {message.category}
                        </span>
                      </div>

                      <blockquote>
                        “{message.message}”
                      </blockquote>

                      <p className="moderationAuthor">
                        — {message.author_name ||
                          "Anonymous"}
                      </p>

                      <div className="reportDetailsList">
                        <p className="reportDetailsHeading">
                          Why people reported it
                        </p>

                        {noteReports.map((report) => (
                          <div
                            className="adminReportItem"
                            key={report.id}
                          >
                            <div className="adminReportItemTop">
                              <strong>
                                {report.reason}
                              </strong>

                              <span>
                                {new Date(
                                  report.created_at
                                ).toLocaleString()}
                              </span>
                            </div>

                            {report.details && (
                              <p>
                                {report.details}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="reportedModerationActions">
                        <button
                          type="button"
                          className="keepNoteButton"
                          disabled={isBusy}
                          onClick={() =>
                            dismissReports(
                              message.id
                            )
                          }
                        >
                          <Check size={17} />

                          {dismissing
                            ? "Keeping..."
                            : "Keep Note"}
                        </button>

                        <button
                          type="button"
                          className="removeNoteButton"
                          disabled={isBusy}
                          onClick={() =>
                            removeReportedNote(
                              message.id
                            )
                          }
                        >
                          <Trash2 size={17} />

                          {removing
                            ? "Removing..."
                            : "Remove From Wall"}
                        </button>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
        </section>

        {/* ----------------------------------------
            BACK TO COMMUNITY WALL
        ---------------------------------------- */}

        <a
          className="adminWallLink"
          href="/"
        >
          <Heart
            size={15}
            fill="currentColor"
          />

          View the Community Wall
        </a>
      </main>
    </div>
  );
}

export default Admin;
